/**
 * Static rewrites for Tailwind class names the model emits incorrectly. Keyed by the
 * bad class, valued by the v4 equivalent. The wrong class produces no CSS rule in v4
 * (Tailwind silently drops unknowns), so without this rewrite the agent's intended
 * styling silently does nothing — most painfully when the wrong class was meant to
 * reset a blur and text ends up unreadable.
 *
 * Add entries only for:
 *  - Documented Tailwind v3 → v4 renames (stable, won't false-positive)
 *  - LLM hallucinations we've seen in production traces (e.g. blur-0)
 *
 * Do NOT use this as a general-purpose typo corrector — every entry is forever and
 * lives outside the agent's learning loop, so keep the surface small.
 */
const LEGACY_CLASS_REWRITES: Readonly<Record<string, string>> = {
  'blur-0': 'blur-none',
};

const WHITESPACE_RE = /\s+/;

// Apply the rewrite to the utility segment (after the final variant `:`) so that
// variant-prefixed forms like `group-data-[slide-active=true]/slide:blur-0` are
// caught too. Tokens whose final segment happens to contain a bad key inside an
// arbitrary value (`data-[state=blur-0]:opacity-50`) are unaffected because the
// utility there is `opacity-50`, not `blur-0`.
function rewriteToken(token: string): string {
  const direct = LEGACY_CLASS_REWRITES[token];
  if (direct !== undefined) return direct;
  for (const [bad, good] of Object.entries(LEGACY_CLASS_REWRITES)) {
    const suffix = `:${bad}`;
    if (token.endsWith(suffix)) {
      return token.slice(0, -bad.length) + good;
    }
  }
  return token;
}

export function normalizeClassName(value: string): string {
  if (!value) return value;
  if (!value.includes(' ')) return rewriteToken(value);
  return value.split(WHITESPACE_RE).map(rewriteToken).join(' ');
}
